import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from './file-info'
import type { EntityHighlightTarget } from '../types/entity-highlight-target'
import { parseImportedBodyEntries } from './body-entries'
import { resolveImportedDisplayName } from './original-node-name'
import { parseGlbBinary } from './glb-binary'

export { parseGlbJsonDocument } from './glb-binary'
import {
  normalizeImportedModelColorOnlyMaterials,
  normalizeImportedModelSurfaceDepthOffsets,
} from './imported-model-material-normalizer'
import { ImportedModelObjectMetadataService } from '../metadata'
import { asRecord } from './utils'
import {
  readNumberMetadataValue as readNumberValue,
  readStringMetadataValue as readStringValue,
} from './metadata-reader'

export type ImportedSemanticKind = 'assembly' | 'part' | 'primitive' | 'unknown'

//------ 线性零部件阵列离线包定义持久化 开始------
/** hierarchy 节点持久化模式。 */
export const GlbHierarchyPersistenceMode = {
  /** 节点由特征定义在运行时派生，不应写入 assembly.json。 */
  RuntimeDerived: 'runtime-derived',
} as const

/** hierarchy 节点持久化模式类型。 */
export type GlbHierarchyPersistenceMode =
  typeof GlbHierarchyPersistenceMode[keyof typeof GlbHierarchyPersistenceMode]
//------ 线性零部件阵列离线包定义持久化 结束------

export interface ImportedBodyEntry {
  bodyId: string
  label: string
  /**
   * Worker GeometryState 中该 body 的权威几何对象 ID。
   *
   * 设计说明：
   * 1. 该值直接复用 BRep 注册链生成的 `geometryObjectId`，不在显示层重新派生；
   * 2. 它同时作为导入基线体的 `modelBodyId/modelBodyRevisionId`，用于特征替换、设计树投影与 Undo/Redo；
   * 3. `bodyBindingId` 仍只描述可见 mesh/装配 occurrence 绑定，不能替代该几何修订身份。
   */
  geometryObjectId?: string
  /**
   * 导入 body 的运行时绑定 ID。
   *
   * 设计说明：
   * 1. 该字段由 loader 在构建 bodyBrepBindings 后回填，供设计树与 GLB 显示层统一识别同一 body；
   * 2. 该字段服务于可见 mesh、拓扑代理与装配 occurrence 的回流，不承担模型体修订身份；
   * 3. 旧包缺少 geometryObjectId 时，边界适配器才允许临时通过该字段解析 Worker 几何身份。
   */
  bodyBindingId?: string
}

export interface GlbLoadResult {
  kind: 'legacy-glb'
  fileInfo: FileInfo
  partName: string
  object: THREE.Group
  gltf: GLTF
  hierarchy: GlbHierarchyNode
  hierarchyRows: GlbHierarchyRow[]
  /** 当前导入结果是否具备跨会话稳定持久化身份。 */
  persistenceStatus?: PuppyCAD.ImportedModelPersistenceStatus
}

export interface GlbHierarchyNode {
  objectId: string
  parentObjectId: string | null
  path: string
  name: string
  nodeType: string
  selectable: boolean
  depth: number
  meta?: GlbHierarchyNodeMeta
  children: GlbHierarchyNode[]
}

export interface GlbHierarchyNodeMeta {
  semanticKind?: ImportedSemanticKind
  /** complete GLB 当前 occurrence 对应的共享 definition 身份。 */
  definitionNodeId?: string
  //------ 线性零部件阵列离线包定义持久化 开始------
  /** 节点是否属于只在运行时重建的派生层级。 */
  persistenceMode?: GlbHierarchyPersistenceMode
  //------ 线性零部件阵列离线包定义持久化 结束------
  /**
   * 当前 hierarchy 节点对应的稳定装配节点 ID。
   *
   * 普通单零件导入时为空。该字段表达 CAD 文档语义身份，不能使用 Object3D.uuid
   * 代替；项目模型装配容器节点写入 nodeKey，Mate 文档、组件位姿快照、
   * 设计树定位和离线包二次导出都依赖它跨加载周期保持稳定。
   */
  assemblyNodeId?: string
  hasGeometry: boolean
  hasDirectGeometry: boolean
  pmi?: Record<string, unknown>
  bodyCount?: number
  bodyEntries?: ImportedBodyEntry[]
  bodyPrimitiveIndicesById?: Record<string, number[]>
  bodyHighlightTargetsById?: Record<string, EntityHighlightTarget[]>
  materialNames: string[]
  materialName?: string
  isFixed?: boolean
  instanceIndex?: number
  configurationName?: string
  displayStateName?: string
  externalFileUuid?: string
  externalAssemblyName?: string
  selectionObjectId?: string
  runtimeWrapper?: boolean
  //------ 线性阵列来源对象粉色高亮 开始------
  /**
   * 线性零部件阵列分组节点关联的源零部件对象 ID 列表。
   *
   * 设计说明：
   * 1. 该字段只写入运行时阵列分组节点；
   * 2. 设计树点击分组时通过它找到源对象 A，用于粉色来源提示；
   * 3. 不改变分组下复制体节点的选择锚点，避免真实选中集合混入源对象。
   */
  linearComponentPatternSourceObjectIds?: string[]
  /**
   * 线性零部件阵列复制体节点对应的源零部件对象 ID。
   *
   * 设计说明：
   * 1. 该字段只写入运行时阵列复制体节点；
   * 2. 当分组元数据缺失时，树投影可从子节点兜底汇总源对象；
   * 3. 字段值使用可在场景中解析的 selection object id，而不是临时树 key。
   */
  linearComponentPatternSourceObjectId?: string
  //------ 线性阵列来源对象粉色高亮 结束------
  /** 该实体对应的 glTF mesh.primitives 下标集合，由 primitiveEntityEntries 解析后写入。
   * 用于设计树 hover/select 时精确高亮该实体对应的几何部分。 */
  highlightTargets?: EntityHighlightTarget[]
}

export interface GlbHierarchyRow {
  fileId: string
  objectId: string
  parentObjectId: string | null
  path: string
  name: string
  nodeType: string
  selectable: boolean
  depth: number
  meta?: GlbHierarchyNodeMeta
}

/**
 * 克隆导入层级节点的运行时元数据。
 *
 * 设计说明：
 * 1. imported-model 的层级镜像现在统一写入 `Object3D.metadata.importedModel`，这里仅保留业务实际消费的导入语义；
 * 2. 采用字段白名单而不是对象展开，避免把 hierarchy 内部状态意外暴露到 Three.js 原生扩展槽；
 * 3. 返回浅克隆，避免调用方与权威 hierarchy 共用可变集合引用。
 *
 * @param meta 权威 hierarchy 元数据。
 * @returns 可写入导入层级镜像的元数据。
 */
export function cloneGlbHierarchyUserDataMeta(meta: GlbHierarchyNodeMeta): GlbHierarchyNodeMeta {
  return {
    semanticKind: meta.semanticKind,
    definitionNodeId: meta.definitionNodeId,
    persistenceMode: meta.persistenceMode,
    assemblyNodeId: meta.assemblyNodeId,
    isFixed: meta.isFixed,
    hasGeometry: meta.hasGeometry,
    hasDirectGeometry: meta.hasDirectGeometry,
    pmi: meta.pmi,
    bodyCount: meta.bodyCount,
    bodyEntries: meta.bodyEntries ? [...meta.bodyEntries] : undefined,
    bodyPrimitiveIndicesById: meta.bodyPrimitiveIndicesById
      ? { ...meta.bodyPrimitiveIndicesById }
      : undefined,
    bodyHighlightTargetsById: meta.bodyHighlightTargetsById
      ? { ...meta.bodyHighlightTargetsById }
      : undefined,
    materialNames: [...(meta.materialNames ?? [])],
    materialName: meta.materialName,
    instanceIndex: meta.instanceIndex,
    configurationName: meta.configurationName,
    displayStateName: meta.displayStateName,
    externalFileUuid: meta.externalFileUuid,
    externalAssemblyName: meta.externalAssemblyName,
    selectionObjectId: meta.selectionObjectId,
    runtimeWrapper: meta.runtimeWrapper,
    linearComponentPatternSourceObjectIds: meta.linearComponentPatternSourceObjectIds
      ? [...meta.linearComponentPatternSourceObjectIds]
      : undefined,
    linearComponentPatternSourceObjectId: meta.linearComponentPatternSourceObjectId,
    highlightTargets: meta.highlightTargets ? [...meta.highlightTargets] : undefined,
  }
}

export function filterGlbFiles<TFile extends FileInfo>(files: TFile[]): TFile[] {
  return files.filter((file) => file.kind === '.glb' && file.uuid)
}

/**
 * 从 project_files 中找到主 CAD 数据文件。
 *
 * 设计说明：OpenProject 不再保证返回 uuid，原生 CAD 下载可通过 fileId + 空 uuid 兜底；
 * 因此这里以 fileId 可用作为可下载判断，避免无 GLB 的 CAD 项目因 uuid 缺失被误判为空项目。
 */
export function findPrimaryCadFile<TFile extends FileInfo>(files: TFile[]): TFile | undefined {
  return (
    files.find((f) => (
      (f.kind === 'cad_part' || f.kind === 'cad_assembly')
      && f.fileId.trim().length > 0
    )) ??
    files.find((f) => f.kind !== 'folder' && f.fileId.trim().length > 0)
  )
}

function normalizeGlbFileKey(fileId: string): string {
  return fileId.replace(/\\/g, '/').trim()
}

export function sortGlbFilesByKey<TFile extends FileInfo>(files: TFile[]): TFile[] {
  return [...filterGlbFiles(files)].sort((a, b) =>
    normalizeGlbFileKey(a.fileId).localeCompare(normalizeGlbFileKey(b.fileId)),
  )
}

export function findPrimaryGlbFile<TFile extends FileInfo>(files: TFile[]): TFile | undefined {
  return sortGlbFilesByKey(files)[0]
}

export function extractPartName(fileId: string): string {
  const normalizedFileId = normalizeGlbFileKey(fileId)
  const fileName = normalizedFileId.split('/').filter(Boolean).at(-1) ?? normalizedFileId
  return fileName.replace(/\.glb$/i, '')
}

export function isAssemblyFile<TFile extends FileInfo>(file: TFile, allGlbFiles: TFile[]): boolean {
  const primary = findPrimaryGlbFile(allGlbFiles)
  return primary?.fileId === file.fileId
}

export function parseGlb(loader: GLTFLoader, data: Uint8Array): Promise<GLTF> {
  return parseGlbBinary(loader, data).then((gltf) => {
    normalizeImportedModelSurfaceDepthOffsets(gltf)
    normalizeImportedModelColorOnlyMaterials(gltf)
    return gltf
  })
}

function buildHierarchySegment(object: THREE.Object3D, siblingIndex: number): string {
  const rawName = object.name?.trim()
  const fallbackName = `${object.type}-${siblingIndex}`
  const displayName = resolveImportedDisplayName(
    object,
    rawName && rawName.length > 0 ? rawName : fallbackName,
  )
  return `${object.type}[${siblingIndex}]:${displayName}`
}

function readBooleanValue(records: Record<string, unknown>[], keys: string[]): boolean | undefined {
  for (const record of records) {
    for (const key of keys) {
      const raw = record[key]
      if (typeof raw === 'boolean') {
        return raw
      }
      if (typeof raw === 'number') {
        if (raw === 1) {
          return true
        }
        if (raw === 0) {
          return false
        }
      }
      if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase()
        if (normalized === 'true' || normalized === '1') {
          return true
        }
        if (normalized === 'false' || normalized === '0') {
          return false
        }
      }
    }
  }
  return undefined
}

function resolveInstanceIndexFromName(name: string): number | undefined {
  const match = name.match(/<(\d+)>/)
  if (!match) {
    return undefined
  }
  const parsed = Number(match[1])
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

/**
 * 判断一个子节点是否为「可跳过的纯几何 primitive 节点」。
 *
 * Puppy CAD 内核 CAD 导出的 Three.js 场景中存在两类子节点：
 *
 * 1. node 组织的语义节点（不可跳过，应展开为设计树子节点）：
 *    对应 glTF JSON nodes[i]，有语义名称（Base、BasePin、StickCylinderInner 等），
 *    自身 userData 携带 importedPartBodyEntries（由 applyNodeBodyEntriesToScene 写入）。
 *    其 bodyEntries 由 resolveNodeDisplayMeta 直接读取，
 *    通过 buildVirtualBodyNodes → buildPartSpecificChildren 生成「实体(N)」文件夹。
 *
 * 2. mesh.primitives 展开的纯几何节点（可跳过）：
 *    Three.js GLTFLoader 加载时，glTF mesh.primitives 中的每个 primitive 会在
 *    node 对应的 Object3D 下创建一个 THREE.Mesh 子节点。
 *    这些子 Mesh【没有】importedPartBodyEntries（body 信息只写在父 node Object3D 上），
 *    不应出现在设计树，只需统计几何数量与材质后忽略。
 *
 * 跳过规则：子节点是 THREE.Mesh 且自身无 importedPartBodyEntries。
 */
function isPrimitiveGeometryNode(object: THREE.Object3D): boolean {
  if (!(object instanceof THREE.Mesh)) {
    return false
  }
  // 有 importedPartBodyEntries 说明是 node 组织的语义节点（不可跳过）
  const entries = object.userData?.importedPartBodyEntries
  return !Array.isArray(entries) || entries.length === 0
}

function collectMeshMaterialNames(mesh: THREE.Mesh): string[] {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  const names = new Set<string>()
  for (const material of materials) {
    if (!material) {
      continue
    }
    const anyMaterial = material as THREE.Material & { name?: string }
    const name = typeof anyMaterial.name === 'string' ? anyMaterial.name.trim() : ''
    if (name.length > 0) {
      names.add(name)
    }
  }
  return Array.from(names)
}

function cloneRecord(value: unknown): Record<string, unknown> | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }
  return { ...record }
}

function resolveStepMetadata(records: Record<string, unknown>[]): Record<string, unknown> | undefined {
  for (const record of records) {
    const stepRecord = asRecord(record.step)
    if (stepRecord) {
      return { ...stepRecord }
    }
  }
  return undefined
}

function resolveStepObjectType(records: Record<string, unknown>[]): ImportedSemanticKind | undefined {
  const stepRecord = resolveStepMetadata(records)
  const value = typeof stepRecord?.objectType === 'string'
    ? stepRecord.objectType.trim().toLowerCase()
    : ''
  if (value === 'assembly' || value === 'part') {
    return value
  }
  return undefined
}

function resolveNodeDisplayMeta(
  object: THREE.Object3D,
): Pick<
  GlbHierarchyNodeMeta,
  | 'semanticKind'
  | 'pmi'
  | 'bodyEntries'
  | 'materialName'
  | 'instanceIndex'
  | 'configurationName'
  | 'displayStateName'
  | 'externalFileUuid'
  | 'externalAssemblyName'
  | 'selectionObjectId'
  | 'runtimeWrapper'
  | 'bodyCount'
> {
  const importedModelMetadata = ImportedModelObjectMetadataService.peekMetadata(object)
  const userDataRecord = asRecord(object.userData) ?? {}
  const metadataRecord = asRecord(userDataRecord.metadata)
  const componentRecord = asRecord(userDataRecord.component)
  const componentInfoRecord = asRecord(userDataRecord.componentInfo)
  const solidworksRecord = asRecord(userDataRecord.solidworks)
  const swRecord = asRecord(userDataRecord.sw)
  const nestedRecords = [
    metadataRecord,
    componentRecord,
    componentInfoRecord,
    solidworksRecord,
    swRecord,
  ].filter((value): value is Record<string, unknown> => Boolean(value))
  const pmiRecords = [
    asRecord(userDataRecord.pmi),
    asRecord(metadataRecord?.pmi),
    asRecord(componentRecord?.pmi),
    asRecord(componentInfoRecord?.pmi),
    asRecord(solidworksRecord?.pmi),
    asRecord(swRecord?.pmi),
  ].filter((value): value is Record<string, unknown> => Boolean(value))
  const records = [userDataRecord, ...nestedRecords]

  const semanticKind = resolveStepObjectType(records)
  const pmi = cloneRecord(
    pmiRecords.find((record) => typeof record.type === 'string') ?? pmiRecords[0],
  )
  const bodyEntries = parseImportedBodyEntries(userDataRecord.importedPartBodyEntries)
  const materialName = readStringValue(records, ['materialName', 'material', 'material_label'])
  const configurationName = readStringValue(records, [
    'configurationName',
    'configuration',
    'configName',
    'config',
  ])
  const displayStateName = readStringValue(records, [
    'displayStateName',
    'displayState',
    'displayStatus',
    'display_status',
  ])
  const rawInstanceIndex = readNumberValue(records, [
    'instanceIndex',
    'instanceNo',
    'instanceNumber',
    'instance',
  ])
  const externalFileUuid = readStringValue(records, [
    'external_file_uuid',
    'externalFileUuid',
  ])
  const externalAssemblyName = readStringValue(records, [
    'external_assembly_name',
    'externalAssemblyName',
  ])
  const selectionObjectId = readStringValue(records, [
    'selectionObjectId',
    'selection_object_id',
  ])
  const runtimeWrapper = readBooleanValue(records, [
    'runtimeWrapper',
  ])
  return {
    semanticKind,
    pmi,
    bodyEntries: bodyEntries.length > 0 ? bodyEntries : undefined,
    materialName,
    instanceIndex: rawInstanceIndex,
    configurationName,
    displayStateName,
    externalFileUuid,
    externalAssemblyName,
    selectionObjectId: importedModelMetadata?.selectionProxyObjectId ?? selectionObjectId,
    runtimeWrapper: importedModelMetadata?.runtimeWrapper ?? runtimeWrapper,
    bodyCount: bodyEntries.length > 0 ? bodyEntries.length : undefined,
  }
}

/**
 * 从 Three.js Object3D 的 userData.primitiveEntityEntries 中，按 bodyId 查找该实体对应的 primitiveIndices。
 * primitiveEntityEntries 由 applyNodeBodyEntriesToScene 写入，格式为
 * Array<{ primitiveIndex: number; bodyId: string; label: string }>。
 */
type PrimitiveEntityEntry = { primitiveIndex: number; bodyId: string; label: string }

function normalizeLocalPrimitiveIndices(indices: number[], primitiveSpan: number): number[] | undefined {
  const normalized = [...new Set(indices)]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < primitiveSpan)
    .sort((a, b) => a - b)
  return normalized.length > 0 ? normalized : undefined
}

function resolveMeshPrimitiveSpan(mesh: THREE.Mesh): number {
  const geometry = mesh.geometry
  if (!(geometry instanceof THREE.BufferGeometry)) {
    return 1
  }
  return geometry.groups.length > 0 ? geometry.groups.length : 1
}

function resolveMeshPrimitiveEntityContext(mesh: THREE.Mesh): {
  entries: PrimitiveEntityEntry[]
  primitiveIndexOffset: number
} {
  let entries: PrimitiveEntityEntry[] = Array.isArray(mesh.userData?.primitiveEntityEntries)
    ? (mesh.userData.primitiveEntityEntries as PrimitiveEntityEntry[])
    : []
  let primitiveIndexOffset = 0

  if (entries.length === 0 && mesh.parent) {
    const parentEntries = Array.isArray(mesh.parent.userData?.primitiveEntityEntries)
      ? (mesh.parent.userData.primitiveEntityEntries as PrimitiveEntityEntry[])
      : []
    if (parentEntries.length > 0) {
      let meshSiblingIndex = 0
      for (const sibling of mesh.parent.children) {
        if (sibling === mesh) {
          break
        }
        if (sibling instanceof THREE.Mesh) {
          meshSiblingIndex += 1
        }
      }
      entries = parentEntries
      primitiveIndexOffset = meshSiblingIndex
    }
  }

  return {
    entries,
    primitiveIndexOffset,
  }
}

function resolveHighlightTargetsForBodyInSubtree(
  rootObject: THREE.Object3D,
  bodyId: string,
): EntityHighlightTarget[] | undefined {
  const targets: EntityHighlightTarget[] = []

  rootObject.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    const primitiveSpan = resolveMeshPrimitiveSpan(object)
    const { entries, primitiveIndexOffset } = resolveMeshPrimitiveEntityContext(object)
    if (entries.length === 0) {
      return
    }

    const localIndices = entries
      .filter((entry) => entry.bodyId === bodyId)
      .map((entry) => entry.primitiveIndex - primitiveIndexOffset)
    const primitiveIndices = normalizeLocalPrimitiveIndices(localIndices, primitiveSpan)
    if (!primitiveIndices) {
      return
    }

    targets.push({
      objectId: object.uuid,
      primitiveIndices,
    })
  })

  return targets.length > 0 ? targets : undefined
}

function buildVirtualBodyNodes(
  parent: {
    objectId: string
    path: string
    depth: number
    selectable: boolean
    meta: GlbHierarchyNodeMeta
    /** 父 Three.js Object3D 的 userData，用于读取 primitiveEntityEntries */
    sourceObject?: THREE.Object3D
  },
): GlbHierarchyNode[] {
  const bodyEntries = Array.isArray(parent.meta.bodyEntries) ? parent.meta.bodyEntries : []
  return bodyEntries.map((bodyEntry, index) => {
    const bodyObjectId = `${parent.objectId}::body::${index + 1}`
    // 按 bodyId 查找该实体对应的 primitiveIndices，用于高亮时精确定位几何
    const highlightTargets = parent.sourceObject
      ? resolveHighlightTargetsForBodyInSubtree(parent.sourceObject, bodyEntry.bodyId)
      : parent.meta.bodyHighlightTargetsById?.[bodyEntry.bodyId]
    const bodyMeta: GlbHierarchyNodeMeta = {
      semanticKind: 'part',
      hasGeometry: parent.meta.hasGeometry,
      hasDirectGeometry: parent.meta.hasDirectGeometry,
      bodyCount: 1,
      bodyEntries: [bodyEntry],
      bodyPrimitiveIndicesById: parent.meta.bodyPrimitiveIndicesById,
      bodyHighlightTargetsById: parent.meta.bodyHighlightTargetsById,
      materialNames: [...parent.meta.materialNames],
      materialName: parent.meta.materialName,
      externalFileUuid: parent.meta.externalFileUuid,
      externalAssemblyName: parent.meta.externalAssemblyName,
      selectionObjectId: parent.objectId,
      highlightTargets,
    }
    return {
      objectId: bodyObjectId,
      parentObjectId: parent.objectId,
      path: `${parent.path}/${bodyEntry.label}`,
      name: bodyEntry.label,
      nodeType: 'Body',
      selectable: parent.selectable,
      depth: parent.depth + 1,
      meta: bodyMeta,
      children: [],
    }
  })
}

export function collectGlbHierarchy(
  root: THREE.Object3D,
  file: FileInfo,
): { hierarchy: GlbHierarchyNode; rows: GlbHierarchyRow[] } {
  const rows: GlbHierarchyRow[] = []
  type VisitResult = {
    node: GlbHierarchyNode
    subtreeMeshCount: number
    materialNames: Set<string>
  }

  const visit = (
    object: THREE.Object3D,
    parentObjectId: string | null,
    parentPath: string,
    depth: number,
    siblingIndex: number,
  ): VisitResult => {
    const segment = buildHierarchySegment(object, siblingIndex)
    const path = parentPath ? `${parentPath}/${segment}` : segment
    const fallbackName = object.name?.trim() || `${object.type}-${siblingIndex}`
    const name = resolveImportedDisplayName(object, fallbackName)
    const isSelfMesh = object instanceof THREE.Mesh
    let directMeshCount = isSelfMesh ? 1 : 0
    let subtreeMeshCount = isSelfMesh ? 1 : 0
    const materialNames = new Set<string>()
    if (isSelfMesh) {
      collectMeshMaterialNames(object).forEach((materialName) => materialNames.add(materialName))
    }

    const children: GlbHierarchyNode[] = []
    object.children.forEach((child, index) => {
      if (child instanceof THREE.Mesh) {
        directMeshCount += 1
      }

      if (isPrimitiveGeometryNode(child)) {
        // glTF mesh.primitives 展开的纯几何 Mesh 子节点：
        // 不创建层级节点，仅统计几何数量与材质。
        // 该 node 节点（父节点）的 bodyEntries 已由 applyNodeBodyEntriesToScene
        // 写入父节点自身 userData，resolveNodeDisplayMeta 会直接读取。
        subtreeMeshCount += 1
        collectMeshMaterialNames(child as THREE.Mesh).forEach((n) => materialNames.add(n))
        return
      }

      const childResult = visit(child, object.uuid, path, depth + 1, index)
      children.push(childResult.node)
      subtreeMeshCount += childResult.subtreeMeshCount
      childResult.materialNames.forEach((materialName) => materialNames.add(materialName))
    })

    const displayMeta = resolveNodeDisplayMeta(object)
    // bodyEntries 由 resolveNodeDisplayMeta 从节点自身 userData.importedPartBodyEntries 读取，
    // 无需额外汇聚。
    const resolvedMaterialNames = Array.from(materialNames)
    const meta: GlbHierarchyNodeMeta = {
      semanticKind: displayMeta.semanticKind ?? 'unknown',
      hasGeometry: subtreeMeshCount > 0,
      hasDirectGeometry: directMeshCount > 0,
      pmi: displayMeta.pmi,
      bodyCount: displayMeta.bodyEntries?.length ?? displayMeta.bodyCount,
      bodyEntries: displayMeta.bodyEntries,
      materialNames: resolvedMaterialNames,
      materialName: displayMeta.materialName,
      instanceIndex: Number.isFinite(displayMeta.instanceIndex)
        ? Math.max(1, Math.round(displayMeta.instanceIndex as number))
        : resolveInstanceIndexFromName(name),
      configurationName: displayMeta.configurationName,
      displayStateName: displayMeta.displayStateName,
      externalFileUuid: displayMeta.externalFileUuid,
      externalAssemblyName: displayMeta.externalAssemblyName,
      selectionObjectId: displayMeta.selectionObjectId,
    }

    const selectable = meta.hasGeometry
    const hierarchyMetadata = {
      fileId: file.fileId,
      name,
      path,
      nodeType: object.type,
      selectable,
      parentObjectId,
      depth,
      meta: cloneGlbHierarchyUserDataMeta(meta),
    }
    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: file.fileId,
      hierarchy: hierarchyMetadata,
      selectable,
    })

    const node: GlbHierarchyNode = {
      objectId: object.uuid,
      parentObjectId,
      path,
      name,
      nodeType: object.type,
      selectable,
      depth,
      meta,
      children,
    }

    const virtualBodyNodes = buildVirtualBodyNodes({
      objectId: node.objectId,
      path: node.path,
      depth: node.depth,
      selectable: node.selectable,
      meta,
      // 传入原始 userData，供 buildVirtualBodyNodes 查找 primitiveEntityEntries
      sourceObject: object,
    })
    if (virtualBodyNodes.length > 0) {
      node.children.push(...virtualBodyNodes)
      virtualBodyNodes.forEach((bodyNode) => {
        rows.push({
          fileId: file.fileId,
          objectId: bodyNode.objectId,
          parentObjectId: bodyNode.parentObjectId,
          path: bodyNode.path,
          name: bodyNode.name,
          nodeType: bodyNode.nodeType,
          selectable: bodyNode.selectable,
          depth: bodyNode.depth,
          meta: bodyNode.meta,
        })
      })
    }
    rows.push({
      fileId: file.fileId,
      objectId: object.uuid,
      parentObjectId,
      path,
      name,
      nodeType: object.type,
      selectable,
      depth,
      meta,
    })
    return {
      node,
      subtreeMeshCount,
      materialNames,
    }
  }

  return { hierarchy: visit(root, null, '', 0, 0).node, rows }
}
